import { Navigate, Outlet, useLocation } from 'react-router';

import { FullPageSpinner } from '@/components/common/full-page-spinner';

import { useAuth } from './auth-provider';
import type { Permission } from './permissions';

/**
 * Gates a route subtree behind a valid session.
 *
 * The `isLoading` branch is what stops the "logged in, hard refresh, briefly
 * bounced to /login, bounced back" flicker: on reload the token exists but the
 * user object has not arrived yet, and treating that as logged out is wrong.
 */
export function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-svh">
        <FullPageSpinner label="Restoring your session" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

/** Keeps an authenticated editor off the login page. */
export function RequireGuest() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-svh">
        <FullPageSpinner label="Checking your session" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

/**
 * Gates a route subtree behind permissions. This is a usability measure, not a
 * security boundary — the API authorises every request independently, because a
 * client-side check is a suggestion to anyone with dev tools.
 */
export function RequirePermission({
  permissions,
  mode = 'any',
}: {
  permissions: readonly Permission[];
  mode?: 'any' | 'all';
}) {
  const { can, canAny } = useAuth();
  const allowed = mode === 'all' ? permissions.every((p) => can(p)) : canAny(permissions);

  if (!allowed) {
    return <Navigate to="/forbidden" replace />;
  }

  return <Outlet />;
}
